import * as React from 'react';
import { Formik } from 'formik';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { TextField } from '../../../components/forms/TextField';
import { Autocomplete } from '../../../components/forms/Autocomplete';
import ButtonWithLoading from '../../../components/forms/SubmitButton';
import { AiAgent } from 'bloggers-backend/lib/aiagents/domain';
import { useEditForm } from '../../../components/forms/hooks/useEditForm';
import { Column } from '../../../components/forms/Column';
import { DeleteFlow } from '../../../components/DeleteFlow';
import { EditFormLoader } from '../../../components/forms/EditFormLoader';
import { AppContext } from '../../../AppContext';
import { useAgentTools } from '../hooks/useAgentTools';
import { useAvailableModels } from '../hooks/useAvailableModels';
import { AgentToolsField } from './AgentToolsField';

const introHint = "An AI agent chats with your team and can call CRM tools on their behalf. " +
  "Describe its role in the system prompt, grant only the tools it needs, and keep it in Draft " +
  "while you test in chat.";
const systemPromptHint = "Define the agent's role, tone, and rules — e.g. 'You help managers draft " +
  "outreach emails to bloggers. Be concise and never send without approval.'";
const modelHint = "Gemini model powering the agent. It can be changed later.";
const toolsHint = "CRM actions the agent may use — grant the minimum needed. Tools marked ⚠ pause " +
  "for human approval on each call.";

const removedModelHint = (model: string) => `${model} is no longer available. Pick another model to save.`;

export default function AiAgentEditForm() {
  const {user} = React.useContext(AppContext);
  const {loading, loadedData, update, isSubmitLoading, validate, deleteEntity} = useEditForm<AiAgent>('aiagents', '/aiagents');
  const [expanded, setExpanded] = React.useState(false);
  const tools = useAgentTools();
  const models = useAvailableModels();
  const modelRemoved = loadedData !== null && models !== undefined && !models.availableModels.includes(loadedData.model);

  return (
    <EditFormLoader loading={loading || !models} loadedData={loadedData}>
      <Formik
        initialValues={modelRemoved ? {...loadedData, model: ''} : loadedData}
        onSubmit={update}
        validate={validate}
      >
        {({values, submitForm}) => (
          <Box sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, px: 2, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">{values.name}</Typography>
              <IconButton onClick={() => setExpanded((value) => !value)} aria-label="toggle agent settings">
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expanded}>
              <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
              <Column>
                <Typography variant="body2" color="text.secondary">{introHint}</Typography>
                <TextField required name="name" label="name"/>
                <TextField name="systemPrompt" label="system prompt" multiline minRows={4} helperText={systemPromptHint}/>
                <Autocomplete required name="model" label="model" options={models?.availableModels ?? []}
                  helperText={modelRemoved ? removedModelHint(loadedData.model) : modelHint}/>
                <AgentToolsField tools={tools} helperText={toolsHint}/>

                <ButtonWithLoading loading={isSubmitLoading} onClick={submitForm}/>
                {
                  user.role === "admin"
                  ? <DeleteFlow deleteWhat="AI Agent" deleteAction={deleteEntity}/>
                  : <></>
                }
              </Column>
              </Box>
            </Collapse>
          </Box>
        )}
      </Formik>
    </EditFormLoader>
  );
}
